import { debounce } from 'lodash'
import dynamic from 'next/dynamic'
import * as R from 'ramda'
import Tabs, { TabPane } from 'rc-tabs'
import TabContent from 'rc-tabs/lib/TabContent'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Waypoint } from 'react-waypoint'
import YouTube from 'react-youtube'
import styled from 'styled-components'

import AspectBox from '../../components/AspectBox'
import TabsStyle from '../../components/Styled/Tabs'
import TabBar from '../../components/TabBar'
import { useTranslation } from '../../i18n'
import { hideHeader } from '../../redux/app/actions'
import { projectSelector } from '../../redux/project/selectors'
import { hk01LinkHandler } from '../../utils/commonUtils'
import {
  detailClickVideo,
  detailReachDescription,
  detailReachTnc
} from '../../utils/dataTracker'
import mq from '../../utils/mediaQuery'

const StyleWrapper = styled.div``
const ProjectDetails = styled.div``
const TabPaneContent = styled.div`
  word-break: break-word;
  padding: 32px 24px;
  img {
    width: 100%;
    max-width: 100%;
  }
  ${mq.tabletMobile} {
    padding: 32px 0;
  }
`
const VidWrapper = styled.div`
  margin-bottom: 24px;
  ${mq.mobile} {
    margin-bottom: 16px;
  }
`
const StyledYouTube = styled(YouTube)`
  position: absolute;
  width: 100%;
  height: 100%;
`
const VidContainer = styled.div`
  height: 100%;
  width: 100%;
  > div,
  > div > div,
  > div > div > video {
    height: 100%;
    width: 100%;
  }
`

const ProjectDescription = ({
  handleContentLinkClick,
  projectNo,
  name,
  description,
  VideoPlayerModule,
  hasVideoPlayerModule,
  hk01VideoIds,
  youtubeVideoIds,
  ...props
}) => {
  return (
    <TabPaneContent>
      {hasVideoPlayerModule &&
        hk01VideoIds.map((vid, index) => (
          <VidWrapper key={`${vid}_${index}`}>
            <AspectBox>
              <VidContainer>
                <VideoPlayerModule
                  onEvent={(category, action, label) => {
                    if (action === 'ClickPlayer') {
                      detailClickVideo(projectNo, name, vid)
                    }
                  }}
                  mediaServerURL={process.env.HK01_VIDEO_SERVER}
                  videoID={vid} // Video ID
                  uiOptions={{
                    aspectRatio: '16:9'
                  }}
                />
              </VidContainer>
            </AspectBox>
          </VidWrapper>
        ))}
      {youtubeVideoIds.map((vid, index) => (
        <VidWrapper key={`${vid}_${index}`}>
          <AspectBox>
            <StyledYouTube
              videoId={vid}
              opts={{ height: '100%', width: '100%' }}
              onPlay={e => {
                detailClickVideo(projectNo, name, vid)
              }}
            />
          </AspectBox>
        </VidWrapper>
      ))}
      <ProjectDetails
        dangerouslySetInnerHTML={{ __html: description }}
        onClick={handleContentLinkClick}
      />
      <Waypoint onEnter={() => detailReachDescription(projectNo, name)} />
    </TabPaneContent>
  )
}

const Caution = ({
  handleContentLinkClick,
  projectNo,
  name,
  tnc,
  ...props
}) => (
  <TabPaneContent>
    <div
      dangerouslySetInnerHTML={{ __html: tnc }}
      onClick={handleContentLinkClick}
    />
    <Waypoint onEnter={() => detailReachTnc(projectNo, name)} />
  </TabPaneContent>
)

const DaySchedule = ({
  handleContentLinkClick,
  projectNo,
  name,
  eventArrangementDetail,
  ...props
}) => (
  <TabPaneContent>
    <div
      dangerouslySetInnerHTML={{ __html: eventArrangementDetail }}
      onClick={handleContentLinkClick}
    />
    <Waypoint onEnter={() => detailReachTnc(projectNo, name)} />
  </TabPaneContent>
)

const Thanks = ({
  handleContentLinkClick,
  projectNo,
  name,
  acknowledgement,
  ...props
}) => (
  <TabPaneContent>
    <div
      dangerouslySetInnerHTML={{ __html: acknowledgement }}
      onClick={handleContentLinkClick}
    />
    <Waypoint onEnter={() => detailReachTnc(projectNo, name)} />
  </TabPaneContent>
)

let VideoPlayerModule = null

const ProjectContent = props => {
  const { t } = useTranslation('common')

  const { projectNo } = props

  const dispatch = useDispatch()

  const [hasVideoPlayerModule, setHasVideoPlayerModule] = useState(false)

  const [showFixedTabBar, setShowFixedTabBar] = useState(false)

  const isAppview = useSelector(R.path(['appState', 'isAppview']))

  const projectData = useSelector(
    state => projectSelector(state, projectNo),
    R.equals
  )

  const { disabled } = props

  const tabContentRef = useRef()

  useEffect(() => {
    VideoPlayerModule = dynamic(() =>
      import('@hk01-digital/hk01-web-video-player')
    )
    setHasVideoPlayerModule(true)
  }, [])

  useEffect(() => {
    dispatch(hideHeader(showFixedTabBar))
  }, [dispatch, showFixedTabBar])

  const getProjectContentDomProps = useCallback(() => {
    const projectContentDomRect = tabContentRef.current.getBoundingClientRect()
    return {
      topOffsetTop: Math.floor(projectContentDomRect.top) - 64 + 46,
      bottomOffsetTop:
        Math.floor(projectContentDomRect.top) +
        projectContentDomRect.height -
        64
    }
  }, [])

  const handleOnScroll = debounce(() => {
    if (!R.isNil(tabContentRef.current)) {
      const { topOffsetTop, bottomOffsetTop } = getProjectContentDomProps()
      const showFixedTabBar = topOffsetTop <= 0 && bottomOffsetTop >= 0
      setShowFixedTabBar(showFixedTabBar)
    }
  }, 200)

  const handleContentLinkClick = useCallback(
    e => {
      !disabled && hk01LinkHandler(e)
    },
    [disabled]
  )

  const getScrollTop = useCallback(
    () => document.documentElement.scrollTop || document.body.scrollTop || 0,
    []
  )

  const handleOnTabChange = useCallback(() => {
    if (!R.isNil(tabContentRef.current)) {
      const { topOffsetTop } = getProjectContentDomProps()
      if (topOffsetTop < 0) {
        const scrollTo = topOffsetTop + getScrollTop()
        document.body.scrollTop = document.documentElement.scrollTop = scrollTo
      }
    }
  }, [])

  useEffect(() => {
    if (!disabled) {
      window.addEventListener('scroll', handleOnScroll)
      return () => {
        if (showFixedTabBar) {
          dispatch(hideHeader(false))
        }
        window.removeEventListener('scroll', handleOnScroll)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, showFixedTabBar])

  if (R.isNil(projectData)) return

  const {
    hk01VideoIds,
    youtubeVideoIds,
    name,
    description,
    tnc,
    eventArrangementDetail,
    acknowledgement
  } = projectData

  return (
    <>
      {/* need to put here for css to override properly */}
      <TabsStyle />
      <StyleWrapper ref={tabContentRef}>
        <Tabs
          defaultActiveKey='1'
          renderTabBar={() => (
            <TabBar
              isAppview={isAppview}
              showFixedTabBar={showFixedTabBar}
              onTabClick={handleOnTabChange}
            />
          )}
          renderTabContent={() => <TabContent />}
        >
          <TabPane tab={t('project.projectContent')} key='1'>
            <ProjectDescription
              handleContentLinkClick={handleContentLinkClick}
              projectNo={projectNo}
              name={name}
              description={description}
              VideoPlayerModule={VideoPlayerModule}
              hasVideoPlayerModule={hasVideoPlayerModule}
              hk01VideoIds={hk01VideoIds}
              youtubeVideoIds={youtubeVideoIds}
            />
          </TabPane>
          {projectData.eventArrangementDetail && (
            <TabPane tab={t('project.daySchedule')} key='2'>
              <DaySchedule
                handleContentLinkClick={handleContentLinkClick}
                projectNo={projectNo}
                name={name}
                eventArrangementDetail={eventArrangementDetail}
              />
            </TabPane>
          )}
          <TabPane tab={t('project.caution')} key='3'>
            <Caution
              handleContentLinkClick={handleContentLinkClick}
              projectNo={projectNo}
              name={name}
              tnc={tnc}
            />
          </TabPane>
          {projectData.acknowledgement && (
            <TabPane tab={t('project.thanks')} key='4'>
              <Thanks
                handleContentLinkClick={handleContentLinkClick}
                projectNo={projectNo}
                name={name}
                acknowledgement={acknowledgement}
              />
            </TabPane>
          )}
        </Tabs>
      </StyleWrapper>
    </>
  )
}

export default React.memo(ProjectContent)
